import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShieldAlert, Phone, AlertTriangle, CheckCircle, ChevronRight, ChevronDown,
  Camera, X, MapPin, Loader, Send, Globe, ClipboardList, BookOpen, Languages,
} from 'lucide-react';
import CameraCapture from './CameraCapture';

// Safety Portal — the driver-facing half.
//
// Exports two surfaces that share ONE payload:
//   • SafetyFocusCard — sits at the top of the My Shift home screen. It is one topic,
//     three lines, and a Got it button. Deliberately small: a driver who is shown
//     everything reads nothing, so the office picks a single focus and that is all
//     the home screen ever shows until it is acknowledged.
//   • SafetyTab       — the full portal tab: who to call, the current focus in full,
//     the address hazard register, the topic shelf, and the report form.
//
// Both render in the driver's chosen language (English · Español · 中文 · Монгол).
// The choice is stored on the device and sent with the acknowledgement, so the office
// can see which language each person was actually briefed in.

const API = '/.netlify/functions/safety-sb';
const LANG_KEY = 'hp_safety_lang';

export const getSafetyLang = () => {
  try { return localStorage.getItem(LANG_KEY) || 'en'; } catch { return 'en'; }
};
const setSafetyLang = (c) => { try { localStorage.setItem(LANG_KEY, c); } catch {} };

// One in-memory payload shared by the home-screen card and the tab, so opening the
// tab after seeing the card costs nothing. Invalidated on language change or ack.
let cache = { lang: null, data: null, at: 0 };
const CACHE_MS = 60_000;

async function post(payload) {
  const res = await fetch(API, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

async function loadPortal(lang, force = false) {
  if (!force && cache.data && cache.lang === lang && Date.now() - cache.at < CACHE_MS) return cache.data;
  const d = await post({ action: 'portal', lang });
  if (d && d.success) cache = { lang, data: d, at: Date.now() };
  return d;
}
const invalidate = () => { cache = { lang: null, data: null, at: 0 }; };

// The illustrated guides are published once per language — /vans-and-cars.html in
// English, /vans-and-cars.es.html in Spanish — by scripts/safety-guides/build.py. A
// driver reading the brief in their own language should get the pictures in it too.
// The slugs are NAMED, not guessed: a guess that is wrong is a 404 in a driver's hand.
// KEEP IN SYNC with SAFETY_GUIDES in netlify/edge-functions/password-gate.js.
const TRANSLATED_GUIDES = [
  'dealing-with-dogs', 'trams-and-tracks', 'vans-and-cars', 'manual-handling', 'fatigue',
  'unsafe-delivery-points', 'heat-and-uv', 'inside-the-facility', 'working-alone',
  'load-shifting-equipment', 'vehicle-security', 'signature-on-delivery',
];
function guideHref(url, lang) {
  if (!url || !lang || lang === 'en') return url || '';
  const m = String(url).match(/^\/([a-z0-9-]+)(?:\.html)?$/);
  if (!m || !TRANSLATED_GUIDES.includes(m[1])) return url;
  return `/${m[1]}.${lang}.html`;
}

// UI copy the driver sees around the translated content. Kept tiny on purpose —
// the safety CONTENT is translated by the engine; these are just the chrome words.
const UI = {
  en: { focus: 'Safety Focus', gotIt: 'Got it', ack: 'Acknowledged', read: 'Read it', call: 'Who to call',
        hazards: 'Hazard register', report: 'Report a safety issue', topics: 'Safety topics',
        thanks: 'Thank you — the office has your report.', send: 'Send report', mine: 'My reports',
        mustRead: 'Read this before you go', readIt: 'I have read this', of: 'of',
        onRound: 'On your round', everyone: 'All drivers', otherStreets: 'Other streets',
        myHazards: 'My hazards', allHazards: 'All hazards', noneForYou: 'Nothing on your round today.',
        guide: 'Illustrated guide' },
  es: { focus: 'Foco de Seguridad', gotIt: 'Entendido', ack: 'Confirmado', read: 'Leer', call: 'A quién llamar',
        hazards: 'Registro de peligros', report: 'Reportar un problema de seguridad', topics: 'Temas de seguridad',
        thanks: 'Gracias — la oficina tiene tu reporte.', send: 'Enviar reporte', mine: 'Mis reportes',
        mustRead: 'Lee esto antes de salir', readIt: 'He leído esto', of: 'de',
        onRound: 'En tu ruta', everyone: 'Todos los conductores', otherStreets: 'Otras calles',
        myHazards: 'Mis peligros', allHazards: 'Todos los peligros', noneForYou: 'Nada en tu ruta hoy.',
        guide: 'Guía ilustrada' },
  zh: { focus: '安全重点', gotIt: '已了解', ack: '已确认', read: '阅读', call: '联系人',
        hazards: '危险地址登记', report: '报告安全问题', topics: '安全主题',
        thanks: '谢谢 — 办公室已收到您的报告。', send: '发送报告', mine: '我的报告',
        mustRead: '出车前必读', readIt: '我已阅读', of: '/',
        onRound: '你的路线上', everyone: '所有司机', otherStreets: '其他街道',
        myHazards: '我的危险地址', allHazards: '全部危险地址', noneForYou: '今天你的路线上没有。',
        guide: '图解指南' },
  mn: { focus: 'Аюулгүй байдлын гол сэдэв', gotIt: 'Ойлголоо', ack: 'Баталгаажсан', read: 'Унших', call: 'Хэнд залгах вэ',
        hazards: 'Аюултай хаягийн бүртгэл', report: 'Аюулгүй байдлын асуудал мэдээлэх', topics: 'Аюулгүй байдлын сэдвүүд',
        thanks: 'Баярлалаа — оффис таны мэдээллийг хүлээн авлаа.', send: 'Мэдээлэл илгээх', mine: 'Миний мэдээллүүд',
        mustRead: 'Гарахаасаа өмнө үүнийг уншина уу', readIt: 'Би үүнийг уншсан', of: '/',
        onRound: 'Таны маршрут дээр', everyone: 'Бүх жолооч', otherStreets: 'Бусад гудамж',
        myHazards: 'Миний аюултай цэгүүд', allHazards: 'Бүх аюултай цэгүүд', noneForYou: 'Өнөөдөр таны маршрут дээр юу ч байхгүй.',
        guide: 'Зурагт заавар' },
};
// Strings for the sign-off brief gate. Separate block so the four languages stay
// readable next to each other.
const GATE = {
  en: { title: 'Safety brief — read this before you start', scroll: 'Scroll to the end to continue',
        done: 'I have read this brief', more: 'more to read on your next shifts', blocked: 'Read the safety brief before you scan your summary.' },
  es: { title: 'Informe de seguridad — léelo antes de empezar', scroll: 'Desliza hasta el final para continuar',
        done: 'He leído este informe', more: 'más para leer en tus próximos turnos', blocked: 'Lee el informe de seguridad antes de escanear tu resumen.' },
  zh: { title: '安全简报 — 出车前必读', scroll: '请滑到最后才能继续',
        done: '我已阅读此简报', more: '份将在接下来的班次阅读', blocked: '扫描汇总前请先阅读安全简报。' },
  mn: { title: 'Аюулгүй байдлын тайлбар — ажлаа эхлэхээсээ өмнө уншина уу', scroll: 'Үргэлжлүүлэхийн тулд эцэс хүртэл гүйлгэнэ үү',
        done: 'Би энэ тайлбарыг уншсан', more: 'дараагийн ажлын өдрүүдэд уншина', blocked: 'Хураангуйг скан хийхээсээ өмнө аюулгүй байдлын тайлбарыг уншина уу.' },
};
const t = (lang, k) => (UI[lang] || UI.en)[k] || UI.en[k];
const g = (lang, k) => (GATE[lang] || GATE.en)[k] || GATE.en[k];

// The authoritative pre-sign-off check. Called by the finish screen at the moment the
// driver taps SUBMIT, not from a payload that may be hours old.
export async function checkBriefGate(lang) {
  try { return await post({ action: 'briefGate', lang: lang || getSafetyLang() }); }
  catch { return { success: false }; }
}

const TONE = { red: 'var(--hp-red)', amber: '#e5a13a', green: 'var(--hp-green)' };

// ── Language picker ───────────────────────────────────────────────────────────
function LangPicker({ langs, lang, onChange, compact }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {(langs || []).map(l => (
        <button key={l.code} onClick={() => onChange(l.code)}
          style={{
            border: `1px solid ${lang === l.code ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
            background: lang === l.code ? 'rgba(201,168,76,0.14)' : 'transparent',
            color: lang === l.code ? 'var(--hp-gold)' : 'var(--hp-dim)',
            borderRadius: 999, padding: compact ? '2px 8px' : '5px 12px',
            fontSize: compact ? '0.66rem' : '0.76rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
          }}>
          {l.flag} {compact ? l.code.toUpperCase() : l.native}
        </button>
      ))}
    </div>
  );
}

// ── "Translate this for me" ───────────────────────────────────────────────────
// A driver who reads Spanish should not have to wait for the office to notice. If the
// brief has no translation in their language, this button makes one — and it is cached
// on the topic, so the next Spanish speaker gets it instantly and for free.
function TranslateButton({ topic, lang, onDone, accent }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const code = lang || 'en';
  if (code === 'en' || !topic || topic.translated) return null;
  const native = (LANG_FALLBACK.find(l => l.code === code) || {}).native || code;

  const go = async () => {
    setBusy(true); setErr('');
    try {
      const d = await post({ action: 'requestTranslation', topicId: topic.id, lang: code });
      if (d && d.success) { invalidate(); onDone && onDone(d.topic); }
      else setErr((d && d.error) || 'Could not translate.');
    } catch (e) { setErr('Could not translate — check your signal.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button onClick={go} disabled={busy}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 'var(--radius)', cursor: 'pointer',
          border: `1px solid ${accent || 'var(--hp-gold)'}`, background: 'transparent',
          color: accent || 'var(--hp-gold)', fontWeight: 700, fontSize: '0.82rem',
          fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
        <Languages size={14} /> {busy ? `Translating into ${native}…` : `Translate into ${native}`}
      </button>
      <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 5, textAlign: 'center' }}>
        {busy ? 'This takes about ten seconds.' : `Shown in English — tap to read it in ${native}.`}
      </div>
      {err && <div style={{ fontSize: '0.74rem', color: 'var(--hp-red)', marginTop: 5 }}>{err}</div>}
    </div>
  );
}

// ── Illustrations ─────────────────────────────────────────────────────────────
// The same pictures as the printed-style guides, chosen per topic in the engine and
// inlined by the backend in the driver's own language (the art draws its own labels).
// A strip that scrolls sideways, so three pictures cost three lines of height and the
// words are still the first thing on the screen.
const escAttr = (s) => String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

function ArtStrip({ items, accent }) {
  if (!items || !items.length) return null;
  const one = items.length === 1;
  return (
    <div style={{
      display: 'flex', gap: 10, overflowX: 'auto', padding: '2px 0 4px', margin: '10px 0 2px',
      scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
    }}>
      {items.map(a => (
        <figure key={a.id} style={{ margin: 0, flex: '0 0 auto', width: one ? '100%' : '80%', scrollSnapAlign: 'start' }}>
          {a.url ? (
            // An uploaded picture — a photo or AP's own artwork, on a short-lived signed URL.
            <img src={a.url} alt={a.caption || ''} style={{
              display: 'block', width: '100%', aspectRatio: '400 / 260', objectFit: 'cover',
              border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: '#fff',
            }} />
          ) : (
            <div
              style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden', background: '#fff' }}
              dangerouslySetInnerHTML={{ __html:
                `<svg viewBox="0 0 400 260" xmlns="http://www.w3.org/2000/svg" role="img" `
                + `aria-label="${escAttr(a.caption)}" style="display:block;width:100%;height:auto">${a.svg}</svg>` }}
            />
          )}
          {/* The guides' own artwork draws its title inside the frame, so a caption under
              it would say the same thing twice. An uploaded picture has no words of its
              own, and that is exactly when the caption earns its place. */}
          {a.url && a.caption && (
            <figcaption style={{
              fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
              color: accent || 'var(--hp-gold)', marginTop: 5, textAlign: 'center', lineHeight: 1.35,
            }}>{a.caption}</figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SAFETY BRIEF GATE — read in the MORNING, before the onboard summary scan
// ══════════════════════════════════════════════════════════════════════════════
// A brief the office has LAUNCHED must be read before the driver can scan their onboard
// summary — i.e. in the MORNING, with fresh eyes. It was on the end-of-shift screen first
// and Paul moved it: tired eyes at the end of a long run skim, and a brief that gets
// skimmed is worse than no brief, because the acknowledgement then lies.
//
// Anti-skim: the button stays disabled until the brief has actually been scrolled to the
// end. Long content therefore takes as long as it takes, and a two-line brief unlocks
// immediately because it has no scroll to do.
// `preview` is the office looking at the same screen from /safety: the component is the
// real one so the preview cannot drift from what a phone shows, but nothing is written —
// no acknowledgement, no device language, no cache to invalidate.
export function SafetyBriefGate({ brief, outstanding, lang, onCleared, onLangChange, preview }) {
  const [busy, setBusy] = useState(false);
  const [readToEnd, setReadToEnd] = useState(false);
  const [previewDone, setPreviewDone] = useState(false);
  const boxRef = useRef(null);
  const code = lang || getSafetyLang();

  // A brief shorter than its own box has nothing to scroll — unlock straight away.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8) setReadToEnd(true);
  }, [brief]);

  if (!brief) return null;
  const accent = brief.accent || 'var(--hp-gold)';

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
  };

  const confirm = async () => {
    if (preview) { setPreviewDone(true); return; }
    setBusy(true);
    try {
      const d = await post({ action: 'ack', topicId: brief.id, lang: code });
      if (d && d.success) { invalidate(); onCleared && onCleared(); }
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      background: 'var(--hp-surface)', border: `2px solid ${accent}`,
      borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16,
    }}>
      <div style={{ background: accent, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <BookOpen size={15} color="#fff" />
        <span style={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff', lineHeight: 1.3 }}>
          {g(code, 'title')}
        </span>
      </div>

      <div style={{ padding: '14px 18px 0' }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.25 }}>
          {brief.emoji} {brief.title}
        </div>
        {brief.subtitle && <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4, lineHeight: 1.45 }}>{brief.subtitle}</div>}
      </div>

      <div ref={boxRef} onScroll={onScroll}
        style={{ maxHeight: 340, overflowY: 'auto', padding: '4px 18px 14px', margin: '10px 0 0', WebkitOverflowScrolling: 'touch' }}>
        <ArtStrip items={brief.artwork} accent={accent} />
        {(brief.sections || []).map(sec => (
          <div key={sec.key} style={{ marginTop: 14 }}>
            <div style={{
              fontSize: '0.63rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: sec.key === 'hazards' ? 'var(--hp-dim)' : accent, marginBottom: 8,
            }}>{sec.label}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(sec.items || []).map((it, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: accent, fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.35, flexShrink: 0 }}>•</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.86rem', color: 'var(--hp-text)', fontWeight: 600, lineHeight: 1.4 }}>{it.t}</div>
                    {it.d && <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.45, whiteSpace: 'pre-line' }}>{it.d}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {brief.guideUrl && (
          <a href={guideHref(brief.guideUrl, code)} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 14, fontSize: '0.8rem', fontWeight: 700, color: accent, textDecoration: 'none' }}>
            <BookOpen size={14} /> {t(code, 'guide')}
          </a>
        )}
      </div>

      <div style={{ padding: '0 18px 16px' }}>
        {/* A compulsory read has to be readable — the language switch refetches the
            brief rather than clearing the gate. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <LangPicker langs={LANG_FALLBACK} lang={code} compact
            onChange={(c) => {
              if (!preview) { setSafetyLang(c); invalidate(); }
              setReadToEnd(false); setPreviewDone(false); onLangChange && onLangChange(c);
            }} />
        </div>
        {/* The office translates from the topic card's Translate button, not from here. */}
        {!preview && <TranslateButton topic={brief} lang={code} accent={accent} onDone={() => onLangChange && onLangChange(code)} />}
        {!readToEnd && (
          <div style={{ fontSize: '0.76rem', color: accent, fontWeight: 700, marginTop: 10, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <ChevronDown size={13} /> {g(code, 'scroll')}
          </div>
        )}
        <button onClick={confirm} disabled={busy || !readToEnd}
          style={{
            width: '100%', padding: '13px', border: 'none', borderRadius: 'var(--radius)',
            background: readToEnd ? accent : 'var(--hp-card)',
            color: readToEnd ? '#fff' : 'var(--hp-dim)',
            fontWeight: 800, fontSize: '0.9rem', cursor: readToEnd ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          }}>
          <CheckCircle size={16} /> {busy ? '…' : g(code, 'done')}
        </button>
        {preview && (
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 8, textAlign: 'center', lineHeight: 1.45 }}>
            {previewDone
              ? 'That is the whole brief — a driver would now be through to their onboard scan. Nothing was recorded.'
              : 'Preview — no acknowledgement is written. A driver meets this before their onboard summary scan.'}
          </div>
        )}
        {!preview && outstanding > 1 && (
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 8, textAlign: 'center' }}>
            {outstanding - 1} {g(code, 'more')}
          </div>
        )}
      </div>
    </div>
  );
}

// The gate can render before any payload has told us the language list.
const LANG_FALLBACK = [
  { code: 'en', label: 'English', flag: '🇦🇺', native: 'English' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸', native: 'Español' },
  { code: 'zh', label: 'Mandarin', flag: '🇨🇳', native: '中文' },
  { code: 'mn', label: 'Mongolian', flag: '🇲🇳', native: 'Монгол' },
];

// ══════════════════════════════════════════════════════════════════════════════
// MUST-READ hazards — the part that cannot be skimmed
// ══════════════════════════════════════════════════════════════════════════════
// An address on the driver's own round, a general hazard, or an address we could not
// match to any route: all three land here. It renders ABOVE everything on the home
// screen, one at a time, and there is no collapse, no dismiss and no X. The only way
// past it is to tap through each one, and each tap is stamped against that driver.
function MustReadHazards({ items, lang, onAcked }) {
  const [busy, setBusy] = useState(false);
  const h = items[0];
  if (!h) return null;
  const col = TONE[h.tone] || TONE.red;

  const readIt = async () => {
    setBusy(true);
    try {
      const d = await post({ action: 'ackHazard', hazardId: h.id, lang });
      if (d && d.success) { invalidate(); onAcked && onAcked(); }
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      width: '100%', maxWidth: 440, background: 'var(--hp-surface)',
      border: `2px solid ${col}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden',
    }}>
      <div style={{ background: col, padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertTriangle size={15} color="#fff" />
        <span style={{ fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff' }}>
          {t(lang, 'mustRead')}
        </span>
        {items.length > 1 && (
          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 800, color: '#fff', opacity: 0.9 }}>
            1 {t(lang, 'of')} {items.length}
          </span>
        )}
      </div>

      <div style={{ padding: '16px 18px' }}>
        {h.scope === 'general' ? (
          <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 6 }}>
            {t(lang, 'everyone')}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
            <MapPin size={16} color={col} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.25 }}>{h.address}</span>
          </div>
        )}

        <div style={{
          display: 'inline-block', background: col, color: '#fff', borderRadius: 999,
          padding: '4px 13px', fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.06em',
        }}>{h.actionLabel}</div>

        <div style={{ fontSize: '0.9rem', color: 'var(--hp-text)', marginTop: 10, lineHeight: 1.5, fontWeight: 600 }}>{h.actionText}</div>
        {h.note && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{h.note}</div>}

        <button onClick={readIt} disabled={busy}
          style={{
            width: '100%', marginTop: 16, padding: '13px', border: 'none', borderRadius: 'var(--radius)',
            background: col, color: '#fff', fontWeight: 800, fontSize: '0.9rem',
            cursor: 'pointer', fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
          }}>
          <CheckCircle size={16} /> {busy ? '…' : t(lang, 'readIt')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Home-screen card
// ══════════════════════════════════════════════════════════════════════════════
export function SafetyFocusCard({ shift, onOpenSafety, onMustReadChange, onBriefsDueChange }) {
  const [lang, setLang] = useState(getSafetyLang);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLang, setShowLang] = useState(false);

  const load = useCallback((force) => {
    loadPortal(lang, force).then(d => { if (d && d.success) setData(d); }).catch(() => {});
  }, [lang]);

  useEffect(() => { load(false); }, [load]);

  const focus = data && data.focus;
  const topic = focus && focus.topic;
  const acks = (data && data.acks) || {};
  const hazards = (data && data.hazards) || [];
  const mustRead = (data && data.mustRead) || [];

  // Feed the count up so the portal can put a red dot on the Safety tab too.
  useEffect(() => { onMustReadChange && onMustReadChange(mustRead.length); }, [mustRead.length, onMustReadChange]);

  // And the compulsory briefs still owed, which the portal adds into the Home Screen
  // tile badge — a brief waiting to be read is a thing waiting on this driver just as
  // much as an open case is. Re-fires after an acknowledgement, so the dot comes down.
  const briefsDue = (data && data.briefsOutstanding) || 0;
  useEffect(() => { onBriefsDueChange && onBriefsDueChange(briefsDue); }, [briefsDue, onBriefsDueChange]);

  // "Acknowledged" means acknowledged since the office set THIS focus — a new focus
  // reopens the card even for a driver who ticked the last one.
  const a = topic ? acks[topic.id] : null;
  const acknowledged = !!(a && focus && (a.last || '').slice(0, 10) >= (focus.since || ''));

  const chooseLang = (code) => { setSafetyLang(code); setLang(code); invalidate(); setShowLang(false); };

  const doAck = async () => {
    if (!topic) return;
    setBusy(true);
    try {
      const d = await post({ action: 'ack', topicId: topic.id, lang });
      if (d && d.success) { invalidate(); load(true); }
    } finally { setBusy(false); }
  };

  // The must-read block goes first, always, whatever else is or is not loaded.
  const forced = mustRead.length
    ? <MustReadHazards items={mustRead} lang={lang} onAcked={() => load(true)} />
    : null;
  // The quiet red strip under the must-read block is for hazards that are THIS driver's and
  // already ticked off — their own round, or something the office pushed to everybody.
  // Somebody else's street is not news on a home screen: a Camperdown address was showing
  // up on every driver's phone, which is how a red strip stops meaning anything. Those
  // still live one tap away, in the Safety page under All hazards. Paul, 31 Aug 2026.
  const seen = hazards
    .filter(h => h.reach === 'mine' || h.reach === 'general' || h.reach === 'all')
    .filter(h => !mustRead.some(m => m.id === h.id));
  const strip = seen.length
    ? <HazardStrip hazards={seen} lang={lang} onOpen={onOpenSafety} />
    : null;

  if (!data || !topic) {
    // Nothing published yet, or still loading — never show an empty shell on the
    // home screen; the shift card matters more. The forced block still shows.
    return <>{forced}{strip}</>;
  }

  const accent = topic.accent || 'var(--hp-gold)';
  const critical = (topic.sections || []).find(s => s.key === 'critical') || (topic.sections || [])[0];
  const lines = ((critical && critical.items) || []).slice(0, 3);

  if (acknowledged) {
    return (
      <>
        {forced}
        <button onClick={onOpenSafety} style={{
          width: '100%', maxWidth: 440, textAlign: 'left', cursor: 'pointer',
          background: 'rgba(46,125,50,0.08)', border: '1px solid rgba(46,125,50,0.35)',
          borderRadius: 'var(--radius-lg)', padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-body)',
        }}>
          <CheckCircle size={16} color="var(--hp-green)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--hp-green)', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t(lang, 'ack')}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--hp-text)', fontWeight: 600, marginTop: 1 }}>
              {topic.emoji} {topic.title}
            </div>
          </div>
          <ChevronRight size={16} color="var(--hp-dim)" />
        </button>
        {strip}
      </>
    );
  }

  return (
    <>
      {forced}
      <div style={{
        width: '100%', maxWidth: 440, background: 'var(--hp-surface)',
        border: `1px solid ${accent}`, borderLeft: `4px solid ${accent}`,
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldAlert size={15} color={accent} />
          <span style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent }}>
            {t(lang, 'focus')}
          </span>
          <button onClick={() => setShowLang(v => !v)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontFamily: 'var(--font-body)' }}>
            <Globe size={13} /> {lang.toUpperCase()}
          </button>
        </div>

        {showLang && (
          <div style={{ padding: '10px 18px 0' }}>
            <LangPicker langs={data.langs} lang={lang} onChange={chooseLang} />
            {!topic.translated && lang !== 'en' && (
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                This topic hasn’t been translated yet — showing English.
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '10px 18px 0' }}>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.2 }}>
            {topic.emoji} {topic.title}
          </div>
          {topic.subtitle && (
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4, lineHeight: 1.45 }}>{topic.subtitle}</div>
          )}
          {focus.note && (
            <div style={{ fontSize: '0.8rem', color: accent, marginTop: 8, lineHeight: 1.45, fontWeight: 600 }}>{focus.note}</div>
          )}
        </div>

        <div style={{ padding: '12px 18px 0', display: 'flex', flexDirection: 'column', gap: 9 }}>
          {lines.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
              <span style={{
                flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: accent,
                color: '#fff', fontSize: '0.68rem', fontWeight: 900,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
              }}>{i + 1}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.88rem', color: 'var(--hp-text)', fontWeight: 600, lineHeight: 1.35 }}>{it.t}</div>
                {it.d && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.4, whiteSpace: 'pre-line' }}>{it.d}</div>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '16px 18px 16px' }}>
          <button onClick={doAck} disabled={busy}
            style={{
              flex: 1, padding: '12px', border: 'none', borderRadius: 'var(--radius)',
              background: accent, color: '#fff', fontWeight: 800, fontSize: '0.88rem',
              cursor: 'pointer', fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
            <CheckCircle size={15} /> {busy ? '…' : t(lang, 'gotIt')}
          </button>
          <button onClick={onOpenSafety}
            style={{
              padding: '12px 16px', borderRadius: 'var(--radius)', cursor: 'pointer',
              border: '1px solid var(--hp-border)', background: 'transparent',
              color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'var(--font-body)',
            }}>
            {t(lang, 'read')}
          </button>
        </div>
      </div>
      {strip}
    </>
  );
}

// A one-line red strip when there are live address hazards — the thing that has to
// reach a driver BEFORE they get to the street.
function HazardStrip({ hazards, lang, onOpen }) {
  const red = hazards.filter(h => h.tone === 'red').length;
  return (
    <button onClick={onOpen} style={{
      width: '100%', maxWidth: 440, textAlign: 'left', cursor: 'pointer',
      background: 'rgba(212,35,47,0.08)', border: '1px solid rgba(212,35,47,0.4)',
      borderRadius: 'var(--radius-lg)', padding: '11px 16px',
      display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-body)',
    }}>
      <AlertTriangle size={16} color="var(--hp-red)" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
          {t(lang, 'hazards')}
          <span style={{ background: 'var(--hp-red)', color: '#fff', borderRadius: 999, padding: '0 7px', fontSize: '0.68rem' }}>{hazards.length}</span>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {red > 0 ? `${red} do-not-enter · ` : ''}{hazards.slice(0, 2).map(h => h.address).join(' · ')}
        </div>
      </div>
      <ChevronRight size={16} color="var(--hp-dim)" />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Full Safety tab
// ══════════════════════════════════════════════════════════════════════════════
export function SafetyTab({ shift, onMustReadChange }) {
  const [lang, setLangState] = useState(getSafetyLang);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('focus');   // focus | contacts | hazards | report
  const [openTopic, setOpenTopic] = useState('');

  const load = useCallback((force) => {
    loadPortal(lang, force)
      .then(d => { if (d && d.success) { setData(d); setError(''); } else setError((d && d.error) || 'Could not load'); })
      .catch(e => setError(e.message));
  }, [lang]);

  useEffect(() => { load(false); }, [load]);

  // Keep the tab's own red dot honest when a hazard is acknowledged from in here.
  const mustReadCount = ((data && data.mustRead) || []).length;
  useEffect(() => { onMustReadChange && onMustReadChange(mustReadCount); }, [mustReadCount, onMustReadChange]);

  const chooseLang = (code) => { setSafetyLang(code); setLangState(code); invalidate(); };

  if (error && !data) return <div style={{ padding: 24, color: 'var(--hp-red)', fontSize: '0.85rem' }}>{error}</div>;
  if (!data) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading…</div>;

  const focusTopic = data.focus && data.focus.topic;
  // Briefs the office has launched and this driver still owes — they block sign-off, so
  // the shelf says so rather than letting it be a surprise at the end of the shift.
  const due = new Set(data.briefsDue || []);

  const VIEWS = [
    { key: 'focus',    label: t(lang, 'topics'),   icon: <BookOpen size={14} /> },
    { key: 'contacts', label: t(lang, 'call'),     icon: <Phone size={14} /> },
    { key: 'hazards',  label: t(lang, 'hazards'),  icon: <AlertTriangle size={14} />, count: (data.mustRead || []).length },
    { key: 'report',   label: t(lang, 'report'),   icon: <Send size={14} /> },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ShieldAlert size={18} color="var(--hp-gold)" />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>Safety</span>
      </div>
      <div style={{ marginBottom: 14 }}>
        <LangPicker langs={data.langs} lang={lang} onChange={chooseLang} />
      </div>

      {/* Same forced block as the home screen — opening this tab does not get you
          past an unread hazard either. */}
      {(data.mustRead || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <MustReadHazards items={data.mustRead} lang={lang} onAcked={() => load(true)} />
        </div>
      )}

      {/* View switcher */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 4, marginBottom: 16, overflowX: 'auto' }}>
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
            style={{
              flex: '1 0 auto', whiteSpace: 'nowrap', padding: '9px 10px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.74rem', fontFamily: 'var(--font-body)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              background: view === v.key ? 'var(--hp-gold)' : 'transparent',
              color: view === v.key ? 'var(--hp-black)' : 'var(--hp-dim)',
            }}>
            {v.icon}{v.label}
            {v.count > 0 && <span style={{ background: 'var(--hp-red)', color: '#fff', borderRadius: 999, padding: '0 6px', fontSize: '0.62rem' }}>{v.count}</span>}
          </button>
        ))}
      </div>

      {view === 'focus' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {data.commitment && (
            <div style={{ border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.55 }}>
              {data.commitment}
            </div>
          )}
          {focusTopic && (
            <TopicCard topic={focusTopic} lang={lang} isFocus open mustRead={due.has(focusTopic.id)}
              acked={data.acks[focusTopic.id]} onAck={() => { invalidate(); load(true); }} />
          )}
          {(data.topics || []).filter(x => !focusTopic || x.id !== focusTopic.id).map(x => (
            <TopicCard key={x.id} topic={x} lang={lang} mustRead={due.has(x.id)}
              open={openTopic === x.id}
              onToggle={() => setOpenTopic(openTopic === x.id ? '' : x.id)}
              acked={data.acks[x.id]} onAck={() => { invalidate(); load(true); }} />
          ))}
        </div>
      )}

      {view === 'contacts' && <Contacts contacts={data.contacts} steps={data.ifItGoesWrong} />}

      {view === 'hazards' && <Hazards hazards={data.hazards} hazardAcks={data.hazardAcks} lang={lang} />}

      {view === 'report' && (
        <ReportForm shift={shift} lang={lang} types={data.reportTypes} onDone={() => { invalidate(); load(true); }} />
      )}
    </div>
  );
}

// ── One topic, expandable ─────────────────────────────────────────────────────
function TopicCard({ topic, lang, isFocus, open: openProp, onToggle, acked, onAck, mustRead }) {
  const [open, setOpen] = useState(!!openProp);
  const [busy, setBusy] = useState(false);
  const accent = topic.accent || 'var(--hp-gold)';
  const isOpen = onToggle ? openProp : open;
  const toggle = onToggle || (() => setOpen(v => !v));

  const doAck = async () => {
    setBusy(true);
    try {
      const d = await post({ action: 'ack', topicId: topic.id, lang });
      if (d && d.success) onAck && onAck();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ border: `1px solid ${isFocus ? accent : 'var(--hp-border)'}`, borderLeft: `4px solid ${accent}`, borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--hp-surface)' }}>
      <button onClick={toggle} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-body)' }}>
        <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{topic.emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isFocus && (
            <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent, marginBottom: 2 }}>
              {t(lang, 'focus')}
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)' }}>{topic.title}</div>
          {mustRead && (
            <div style={{ display: 'inline-block', marginTop: 4, background: 'var(--hp-red)', color: '#fff', borderRadius: 999, padding: '2px 9px', fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {g(lang, 'title')}
            </div>
          )}
          {topic.subtitle && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.4 }}>{topic.subtitle}</div>}
        </div>
        {acked && <CheckCircle size={15} color="var(--hp-green)" style={{ flexShrink: 0 }} />}
        {isOpen ? <ChevronDown size={16} color="var(--hp-dim)" /> : <ChevronRight size={16} color="var(--hp-dim)" />}
      </button>

      {isOpen && (
        <div style={{ padding: '0 16px 16px' }}>
          <ArtStrip items={topic.artwork} accent={accent} />
          {(topic.sections || []).map(sec => (
            <div key={sec.key} style={{ marginTop: 14 }}>
              <div style={{
                fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: sec.key === 'hazards' ? 'var(--hp-dim)' : accent, marginBottom: 8,
              }}>{sec.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(sec.items || []).map((it, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: accent, fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.35, flexShrink: 0 }}>•</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.86rem', color: 'var(--hp-text)', fontWeight: 600, lineHeight: 1.4 }}>{it.t}</div>
                      {it.d && <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.45, whiteSpace: 'pre-line' }}>{it.d}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {topic.guideUrl && (
            <a href={guideHref(topic.guideUrl, lang)} target="_blank" rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 14, fontSize: '0.8rem', fontWeight: 700, color: 'var(--hp-gold)', textDecoration: 'none' }}>
              <BookOpen size={14} /> {t(lang, 'guide')}
            </a>
          )}

          <TranslateButton topic={topic} lang={lang} accent={accent} onDone={onAck} />

          <button onClick={doAck} disabled={busy}
            style={{
              width: '100%', marginTop: 16, padding: '11px', borderRadius: 'var(--radius)', cursor: 'pointer',
              border: acked ? '1px solid var(--hp-border)' : 'none',
              background: acked ? 'transparent' : accent,
              color: acked ? 'var(--hp-dim)' : '#fff',
              fontWeight: 800, fontSize: '0.85rem', fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
            <CheckCircle size={15} /> {acked ? t(lang, 'ack') : t(lang, 'gotIt')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Who to call ───────────────────────────────────────────────────────────────
function Contacts({ contacts, steps }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(steps || []).length > 0 && (
        <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 4 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i === steps.length - 1 ? 0 : 10 }}>
              <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: 'var(--hp-gold)', color: 'var(--hp-black)', fontSize: '0.68rem', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>{i + 1}</span>
              <div>
                <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--hp-text)' }}>{s.step}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.45 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(contacts || []).map(c => {
        const urgent = c.id === 'emergency';
        return (
          <a key={c.id} href={c.phone ? `tel:${c.phone.replace(/\s/g, '')}` : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
              border: `1px solid ${urgent ? 'var(--hp-red)' : 'var(--hp-border)'}`,
              background: urgent ? 'rgba(212,35,47,0.08)' : 'var(--hp-surface)',
              borderRadius: 'var(--radius)', padding: '13px 15px',
              opacity: c.phone ? 1 : 0.55, pointerEvents: c.phone ? 'auto' : 'none',
            }}>
            <Phone size={17} color={urgent ? 'var(--hp-red)' : 'var(--hp-gold)'} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.role}</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 1 }}>
                {c.name}{c.phone ? ` · ${c.phone}` : ' · number not set'}
              </div>
              {c.when && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.4 }}>{c.when}</div>}
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ── Address hazard register ───────────────────────────────────────────────────
// Three groups, in the order they matter to this driver: what is on their own round,
// what applies to everyone, and other people's streets (there for awareness, and
// because a driver covering someone's day off needs to be able to look them up).
function Hazards({ hazards, hazardAcks, lang }) {
  const [view, setView] = useState('mine');
  if (!hazards || !hazards.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}>
        No live hazards. Addresses reported as unsafe appear here with what to do.
      </div>
    );
  }
  // MINE is what actually applies to this driver today: their own round, plus anything the
  // office deliberately pushed to everybody. ALL is the whole register — other people's
  // streets included — so a driver sent somewhere unusual can still look an address up.
  // Splitting them is the point: an address two suburbs away used to sit in the same list
  // as their own round, so the list stopped being read. Paul, 31 Aug 2026.
  const isMine = (h) => h.reach === 'mine' || h.reach === 'general' || h.reach === 'all';
  const groups = view === 'mine'
    ? [
      ['mine', t(lang, 'onRound'), hazards.filter(h => h.reach === 'mine')],
      ['general', t(lang, 'everyone'), hazards.filter(h => h.reach === 'general' || h.reach === 'all')],
    ].filter(([, , list]) => list.length)
    : [
      ['mine', t(lang, 'onRound'), hazards.filter(h => h.reach === 'mine')],
      ['general', t(lang, 'everyone'), hazards.filter(h => h.reach === 'general' || h.reach === 'all')],
      ['other', t(lang, 'otherStreets'), hazards.filter(h => h.reach === 'other' || h.reach === 'unrouted')],
    ].filter(([, , list]) => list.length);

  const mineCount = hazards.filter(isMine).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {[['mine', `${t(lang, 'myHazards')} (${mineCount})`], ['all', `${t(lang, 'allHazards')} (${hazards.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} style={{
            flex: 1, padding: '9px 10px', borderRadius: 'var(--radius)', cursor: 'pointer',
            border: `1px solid ${view === k ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
            background: view === k ? 'var(--hp-gold)' : 'transparent',
            color: view === k ? 'var(--hp-black)' : 'var(--hp-dim)',
            fontWeight: 800, fontSize: '0.78rem',
          }}>{label}</button>
        ))}
      </div>

      {view === 'mine' && !mineCount && (
        <div style={{ padding: 22, textAlign: 'center', color: 'var(--hp-dim)', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}>
          {t(lang, 'noneForYou')}
        </div>
      )}

      {groups.map(([key, label, list]) => (
        <div key={key}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <span style={{
              fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: key === 'mine' ? 'var(--hp-red)' : 'var(--hp-dim)',
            }}>{label}</span>
            <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
            <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>{list.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {list.map(h => {
              const col = TONE[h.tone] || TONE.amber;
              const a = (hazardAcks || {})[h.id];
              const dim = key === 'other';
              return (
                <div key={h.id} style={{
                  border: `1px solid ${dim ? 'var(--hp-border)' : col}`, borderLeft: `4px solid ${col}`,
                  borderRadius: 'var(--radius)', padding: '13px 15px', background: 'var(--hp-surface)',
                  opacity: dim ? 0.75 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                    {h.scope === 'general'
                      ? <AlertTriangle size={14} color={col} style={{ flexShrink: 0, marginTop: 3 }} />
                      : <MapPin size={14} color={col} style={{ flexShrink: 0, marginTop: 3 }} />}
                    <span style={{ fontSize: '0.92rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.3 }}>
                      {h.address || h.note || h.actionLabel}
                    </span>
                  </div>
                  <div style={{
                    display: 'inline-block', marginTop: 9, background: col, color: '#fff',
                    borderRadius: 999, padding: '3px 11px', fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.06em',
                  }}>{h.actionLabel}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', marginTop: 8, lineHeight: 1.5 }}>{h.actionText}</div>
                  {h.note && h.address && <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{h.note}</div>}
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {h.since && <span>On the register since {h.since}</span>}
                    {a && (
                      <span style={{ color: 'var(--hp-green)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <CheckCircle size={11} /> {t(lang, 'ack')} {fmtWhen(a.last)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return ''; }
};

// ── Report a safety issue ─────────────────────────────────────────────────────
function ReportForm({ shift, lang, types, onDone }) {
  const [type, setType] = useState('');
  const [occurredOn, setOccurredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [occurredAt, setOccurredAt] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [injured, setInjured] = useState(false);
  const [injuryDetail, setInjuryDetail] = useState('');
  const [witnesses, setWitnesses] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [captureKey, setCaptureKey] = useState(0);
  const [coords, setCoords] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [mine, setMine] = useState(null);
  const loadedMine = useRef(false);

  useEffect(() => {
    if (loadedMine.current) return;
    loadedMine.current = true;
    post({ action: 'myReports' }).then(d => { if (d && d.success) setMine(d.reports); }).catch(() => {});
  }, []);

  const selected = (types || []).find(x => x.id === type);

  const useGps = () => {
    if (!navigator.geolocation) return;
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords: c }) => {
        setCoords({ lat: c.latitude, lng: c.longitude });
        if (!address.trim()) setAddress(`${c.latitude.toFixed(5)}, ${c.longitude.toFixed(5)}`);
        setGpsBusy(false);
      },
      () => setGpsBusy(false),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const addPhoto = (dataUrl) => { setPhotos(p => [...p, dataUrl]); setCaptureKey(k => k + 1); };

  const submit = async () => {
    if (!type) { setError('Choose what you are reporting.'); return; }
    if (!description.trim()) { setError('Please describe what happened.'); return; }
    setError(''); setBusy(true);
    try {
      const d = await post({
        action: 'submitReport',
        type, lang,
        driverName: (shift && shift.name) || '',
        occurredOn, occurredAt,
        address: address.trim(),
        latitude: coords ? coords.lat : null,
        longitude: coords ? coords.lng : null,
        description: description.trim(),
        actionTaken: actionTaken.trim(),
        injured, injuryDetail: injuryDetail.trim(),
        witnesses: witnesses.filter(w => (w.name || '').trim() || (w.phone || '').trim()),
        photos: photos.map(p => p.split(',')[1]).filter(Boolean),
      });
      if (!d || !d.success) { setError((d && d.error) || 'Could not send the report.'); return; }
      setDone(true);
      onDone && onDone();
    } catch (e) {
      setError('Could not send — check your signal and try again.');
    } finally { setBusy(false); }
  };

  const F = {
    input: { width: '100%', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontSize: '0.95rem', padding: '11px 13px', outline: 'none', fontFamily: 'var(--font-body)', WebkitAppearance: 'none' },
    label: { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, marginTop: 16 },
  };

  if (done) {
    return (
      <div style={{ border: '1px solid var(--hp-green)', borderRadius: 'var(--radius)', padding: 22, textAlign: 'center' }}>
        <CheckCircle size={34} color="var(--hp-green)" />
        <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 10 }}>{t(lang, 'thanks')}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.5 }}>
          If it is an address, we will put a standing instruction on the register so every driver sees it.
          If anyone is hurt, call the office now — do not wait for us to read this.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: 1.55, marginBottom: 4 }}>
        Anything unsafe — a dog, an address, a near miss, an injury. You will never be in trouble for reporting.
        <strong style={{ color: 'var(--hp-text)' }}> If anyone is hurt or threatened, call 000 first.</strong>
      </div>

      <label style={F.label}>What are you reporting?</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {(types || []).map(x => (
          <button key={x.id} onClick={() => setType(x.id)}
            style={{
              border: `1px solid ${type === x.id ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
              background: type === x.id ? 'rgba(201,168,76,0.14)' : 'var(--hp-surface)',
              color: type === x.id ? 'var(--hp-gold)' : 'var(--hp-text)',
              borderRadius: 999, padding: '8px 13px', fontSize: '0.8rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>
            {x.emoji} {x.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={F.label}>Date</label>
          <input style={F.input} type="date" value={occurredOn} onChange={e => setOccurredOn(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={F.label}>Time</label>
          <input style={F.input} type="time" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} />
        </div>
      </div>

      <label style={F.label}>
        Address {selected && selected.wantsAddress ? '· required for the hazard register' : '· if it has one'}
      </label>
      <input style={F.input} value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. 42 Victoria Street, Potts Point" />
      <button onClick={useGps} disabled={gpsBusy}
        style={{ marginTop: 7, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', padding: '7px 12px', fontSize: '0.76rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
        <MapPin size={13} /> {gpsBusy ? 'Locating…' : 'Use my location'}
      </button>

      <label style={F.label}>What happened?</label>
      <textarea style={{ ...F.input, minHeight: 100, resize: 'vertical' }} value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Say it plainly — what you saw, what it did, what you did." />

      <label style={F.label}>What did you do at the time?</label>
      <input style={F.input} value={actionTaken} onChange={e => setActionTaken(e.target.value)}
        placeholder="e.g. Carded it and took it to the Post Office" />

      <label style={F.label}>Was anyone hurt?</label>
      <div style={{ display: 'flex', gap: 7 }}>
        {[['no', 'No'], ['yes', 'Yes']].map(([k, label]) => (
          <button key={k} onClick={() => setInjured(k === 'yes')}
            style={{
              flex: 1, border: `1px solid ${injured === (k === 'yes') ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
              background: injured === (k === 'yes') ? 'rgba(201,168,76,0.14)' : 'var(--hp-surface)',
              color: injured === (k === 'yes') ? 'var(--hp-gold)' : 'var(--hp-text)',
              borderRadius: 'var(--radius)', padding: '10px', fontSize: '0.85rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>{label}</button>
        ))}
      </div>
      {injured && (
        <>
          <label style={F.label}>Who, and what injury?</label>
          <input style={F.input} value={injuryDetail} onChange={e => setInjuryDetail(e.target.value)}
            placeholder="e.g. Me — bite to left forearm, saw a doctor" />
        </>
      )}

      <label style={F.label}>Witnesses</label>
      {witnesses.map((w, i) => (
        <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 7 }}>
          <input style={{ ...F.input, flex: 1.2 }} placeholder="Name" value={w.name || ''}
            onChange={e => setWitnesses(ws => ws.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
          <input style={{ ...F.input, flex: 1 }} placeholder="Phone" inputMode="tel" value={w.phone || ''}
            onChange={e => setWitnesses(ws => ws.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} />
          <button onClick={() => setWitnesses(ws => ws.filter((_, j) => j !== i))}
            style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', cursor: 'pointer', padding: '0 10px' }}>
            <X size={14} />
          </button>
        </div>
      ))}
      {witnesses.length < 6 && (
        <button onClick={() => setWitnesses(ws => [...ws, { name: '', phone: '' }])}
          style={{ background: 'none', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', padding: '9px 13px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-body)', width: '100%' }}>
          + Add a witness
        </button>
      )}

      <label style={F.label}><Camera size={13} style={{ verticalAlign: -2 }} /> Photos ({photos.length})</label>
      {photos.length > 0 && (
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={p} alt={`Photo ${i + 1}`} style={{ width: 78, height: 78, objectFit: 'cover', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)' }} />
              <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))} aria-label="Remove photo"
                style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 999, border: 'none', background: 'var(--hp-red)', color: '#fff', cursor: 'pointer', fontSize: '0.7rem' }}>×</button>
            </div>
          ))}
        </div>
      )}
      {photos.length < 8 && (
        <CameraCapture key={captureKey} nativeFirst guide="document" maxEdge={1280} quality={0.78} onCapture={addPhoto} />
      )}

      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.82rem', marginTop: 14 }}>{error}</div>}

      <button onClick={submit} disabled={busy}
        style={{
          width: '100%', marginTop: 18, padding: '14px', border: 'none', borderRadius: 'var(--radius)',
          background: 'var(--hp-red)', color: '#fff', fontWeight: 800, fontSize: '0.95rem',
          cursor: 'pointer', fontFamily: 'var(--font-body)', opacity: busy ? 0.6 : 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        }}>
        <Send size={16} /> {busy ? 'Sending…' : t(lang, 'send')}
      </button>

      {mine && mine.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <ClipboardList size={14} color="var(--hp-dim)" />
            <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{t(lang, 'mine')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mine.slice(0, 8).map(r => (
              <div key={r.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '11px 13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{r.occurred_on}</span>
                  <span style={{
                    fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: r.status === 'closed' ? 'var(--hp-green)' : r.status === 'new' ? 'var(--hp-gold)' : 'var(--hp-text)',
                    border: '1px solid currentColor', borderRadius: 999, padding: '1px 8px',
                  }}>{r.status}</span>
                  {r.hazard_id && <span style={{ fontSize: '0.62rem', color: 'var(--hp-red)', fontWeight: 800 }}>ON REGISTER</span>}
                </div>
                {r.address && <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 4 }}>{r.address}</div>}
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.45 }}>{r.description}</div>
                {r.outcome && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--hp-green)', marginTop: 6, lineHeight: 1.45 }}>
                    <strong>Outcome:</strong> {r.outcome}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SafetyTab;
